/** @jsxImportSource @emotion/react */
import Script from 'next/script'
import getConfig from 'next/config'
import { useRouter } from 'next/router'
import { Box, Heading } from 'grommet'
import { useTranslation } from 'next-i18next'
import { FormPreviousLink } from 'grommet-icons'
import { useCallback, useMemo, useRef, useState } from 'react'

import { routes } from 'routes'
import { useAuth } from 'hooks/useAuth'
import { trackEvent } from 'utils/useGTM'
import LinkAnchor from 'components/LinkAnchor'
import CoverSpinner from 'components/CoverSpinner'
import ComparePlans from './ComparePlans'

const {
  publicRuntimeConfig: { PADDLE_SANDBOX, PADDLE_VENDOR_ID },
} = getConfig()

export default function SubscriptionPlans() {
  const { user } = useAuth()
  const { t } = useTranslation()
  const router = useRouter()
  const paddleContainer = useRef<HTMLDivElement>(null)

  const [checkoutLoaded, setCheckoutLoaded] = useState(false)
  const [productIdLoading, setProductIdLoading] = useState<string | false>(false)

  const openSettings = useMemo(
    () => ({
      method: 'inline',
      frameTarget: 'checkout-container',
      frameInitialHeight: 416,
      frameStyle: 'width:100%; min-width:312px; background-color: transparent; border: none;',
      passthrough: JSON.stringify({ id: user?.id, name: user?.user_metadata?.full_name }),
      email: user?.email,
    }),
    [user],
  )

  const handleCheckout = useCallback(
    async (product: string) => {
      if (user?.app_metadata?.plan?.subscription_plan_id === product) {
        // Manage
        window.open(user?.app_metadata?.plan?.update_url)
      } else {
        // Subscribe
        setProductIdLoading(product)
        window.Paddle.Checkout.open({ ...openSettings, product })
      }
    },
    [
      openSettings,
      user?.app_metadata?.plan?.subscription_plan_id,
      user?.app_metadata?.plan?.update_url,
    ],
  )

  return (
    <section>
      <Script
        key="init-paddle"
        src="https://cdn.paddle.com/paddle/paddle.js"
        onLoad={() => {
          if (PADDLE_SANDBOX) {
            window.Paddle.Environment.set('sandbox')
          }
          window.Paddle.Setup({
            vendor: Number.parseInt(PADDLE_VENDOR_ID),
            eventCallback: function (data) {
              // The data.event will specify the event type
              trackEvent({ event: data.event })
              setCheckoutLoaded(true)
              if (data.event === 'Checkout.Loaded') {
                setProductIdLoading(false)
              } else if (data.event === 'Checkout.Complete') {
                // console.log(data.eventData) // Data specifics on the event
                router.push(routes.home)
              } else if (data.event === 'Checkout.Close') {
                // console.log(data.eventData) // Data specifics on the event
              }
            },
          })
        }}
      />

      {!checkoutLoaded ? (
        <ComparePlans handleCheckout={handleCheckout} productIdLoading={productIdLoading} />
      ) : (
        <Heading fill margin={{ top: 'large', bottom: 'medium' }} textAlign="center" size="2.5rem">
          {'Pricing adjusted for your audience.'}
        </Heading>
      )}
      {productIdLoading && <CoverSpinner />}
      {checkoutLoaded && (
        <Box
          width={{ max: 'large' }}
          pad={{ horizontal: 'large' }}
          margin={{ horizontal: 'auto', bottom: 'large' }}>
          <LinkAnchor
            href={routes.pricing}
            onClick={() => setCheckoutLoaded(false)}
            size="small"
            color="gray"
            css={{ display: 'flex', justifyContent: 'start', alignContent: 'center' }}>
            <FormPreviousLink />
            {t('cta.change-plan')}
          </LinkAnchor>
        </Box>
      )}
      <Box margin={{ horizontal: 'large' }}>
        <div key="checkout-container" ref={paddleContainer} className="checkout-container" />
      </Box>
    </section>
  )
}
